import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { SERVICES } from "../mock";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services", hasMega: true },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const location = useLocation();
  const settings = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMega(false); }, [location.pathname]);

  const isHome = location.pathname === "/";
  const overDark = isHome && !scrolled;

  return (
    <>
      {/* Top strip */}
      <div className="hidden lg:block bg-ink text-white/85 text-[11px] tracking-[0.22em] uppercase">
        <div className="max-w-[1440px] mx-auto px-8 flex items-center justify-between py-2.5">
          <div className="flex items-center gap-6">
            <span>— {settings?.address_line3 || "223 Regent Street"}, {settings?.address_city || "London"}</span>
            {/* <span>{settings?.hours_weekdays || "Mon–Fri 08–18"}</span> */}
          </div>
          <div className="flex items-center gap-6">
            <a href={`tel:${settings?.phone || ""}`} className="hover:text-amber transition-colors">{settings?.phone || "020 7544 1449"}</a>
            <span className="text-white/35">•</span>
            <a href={`mailto:${settings?.email || ""}`} className="hover:text-amber transition-colors">{settings?.email || "info@rsconstructionhi.co.uk"}</a>
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-40 transition-[background,box-shadow,color] duration-500 ${
        overDark ? "bg-transparent" : "bg-bone/95 backdrop-blur-md border-b border-line"
      }`}>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 flex items-center justify-between h-[78px]">
          <Link to="/" className="flex items-center gap-3 group">
            <span className={`font-display-tight text-[28px] leading-none ${overDark ? "text-bone" : "text-ink"}`}>
              RS<span className="text-amber italic">.</span>
            </span>
            <div className="hidden md:block leading-tight pl-3 border-l border-current/20">
              <div className={`text-[13px] tracking-[0.02em] ${overDark ? "text-bone" : "text-ink"}`}>Construction</div>
              <div className={`text-[9px] tracking-[0.3em] uppercase ${overDark ? "text-bone/60" : "text-ink/50"}`}>HI Ltd · Est. 1998</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {LINKS.map((n) => (
              <div key={n.to} className="relative" onMouseEnter={() => n.hasMega && setMega(true)} onMouseLeave={() => setMega(false)}>
                <NavLink to={n.to} end={n.to === "/"}
                  className={({ isActive }) =>
                    `relative text-[12px] tracking-[0.22em] uppercase font-semibold link-line transition-colors ${
                      overDark ? "text-white hover:text-amber" : "text-ink hover:text-amber"
                    } ${isActive ? "text-amber" : ""}`
                  }>
                  {n.label}
                </NavLink>
                {n.hasMega && mega && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-6 z-40">
                    <div className="w-[720px] bg-bone border border-line shadow-2xl grid grid-cols-2">
                      <div className="col-span-2 grid grid-cols-2 gap-x-2 gap-y-0 p-3">
                        {SERVICES.map((s) => (
                          <Link key={s.slug} to={`/services/${s.slug}`}
                            className="group flex items-center justify-between px-4 py-3 hover:bg-bone-2 transition-colors">
                            <div>
                              <div className="text-[9px] tracking-[0.3em] uppercase text-ink/40">{s.number}</div>
                              <div className="font-display text-[18px] text-ink">{s.title}</div>
                            </div>
                            <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 text-amber transition-opacity" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a href={`tel:${settings?.phone || ""}`} className={`flex items-center gap-2 text-[13px] tracking-wide ${overDark ? "text-bone" : "text-ink"}`}>
              <Phone className="w-3.5 h-3.5" /> {settings?.phone || "020 7544 1449"}
            </a>
            <Link to="/contact" className={`btn-magnetic px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold border ${
              overDark ? "bg-amber text-ink border-amber hover:bg-bone hover:border-bone" : "bg-ink text-bone border-ink hover:bg-amber hover:text-ink hover:border-amber"
            }`}>
              Start a Project
            </Link>
          </div>

          <button aria-label="menu" onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 ${overDark ? "text-bone" : "text-ink"}`}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-bone border-t border-line">
            <nav className="px-6 py-5 flex flex-col divide-y divide-line">
              {LINKS.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"}
                  className={({ isActive }) => `py-4 font-display text-2xl text-ink ${isActive ? "text-amber" : ""}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/contact" className="mt-5 bg-ink text-bone text-center py-4 tracking-[0.22em] uppercase text-[11px] font-semibold">
                Start a Project
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
